import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
    FaExternalLinkAlt, FaArrowLeft, FaCheckCircle, FaFileAlt,
    FaInfoCircle, FaPhone, FaEnvelope, FaGlobe, FaSpinner,
    FaExclamationTriangle, FaClipboardList, FaChevronRight,
    FaChevronLeft, FaImage, FaTimes, FaFilePdf, FaDownload
} from 'react-icons/fa';
import { frontendMenuService } from '../../services/frontendMenuService';
import { getAssetUrl } from '../../services/api';

const LayananRedirect = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [menu, setMenu] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // State untuk slider
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    // State untuk modal popup slider
    const [isModalOpen, setIsModalOpen] = useState(false);

    useEffect(() => {
        loadMenu();
        window.scrollTo(0, 0);
    }, [slug]);

    const loadMenu = async () => {
        setLoading(true);
        const result = await frontendMenuService.getMenuBySlug(slug);
        if (result.success) {
            const menuData = result.data?.data || result.data;
            setMenu(menuData);

            // Buka modal otomatis jika ada gambar
            const imgs = Array.isArray(menuData?.image_paths) ? menuData.image_paths : [];
            if (imgs.length > 0) {
                setIsModalOpen(true);
            }
        } else {
            setError(result.message || 'Menu tidak ditemukan');
        }
        setLoading(false);
    };

    const handleOpenExternal = () => {
        if (menu?.external_url) {
            window.open(menu.external_url, '_blank', 'noopener,noreferrer');
        }
    };

    // Ambil array gambar dari menu
    const images = Array.isArray(menu?.image_paths) ? menu.image_paths : [];

    // Navigasi slider
    const goToPrevImage = () => {
        setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    };

    const goToNextImage = () => {
        setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    };

    const goToImage = (index) => {
        setCurrentImageIndex(index);
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    // Handle ESC key & body scroll lock saat modal terbuka
    useEffect(() => {
        if (!isModalOpen) return;

        const handleKeyDown = (e) => {
            if (e.key === 'Escape') closeModal();
        };

        document.addEventListener('keydown', handleKeyDown);
        // Lock scroll body
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = originalOverflow;
        };
    }, [isModalOpen]);

    if (loading) {
        return (
            <div className="container mx-auto px-4 py-20 text-center">
                <FaSpinner className="animate-spin text-5xl text-[#2E7D32] mx-auto mb-4" />
                <p className="text-gray-500">Memuat informasi layanan...</p>
            </div>
        );
    }

    if (error || !menu) {
        return (
            <div className="container mx-auto px-4 py-12">
                <div className="card-glass text-center py-12 max-w-2xl mx-auto">
                    <FaExclamationTriangle className="text-6xl text-yellow-500 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-gray-700 mb-2">Halaman Tidak Ditemukan</h2>
                    <p className="text-gray-500 mb-6">{error || 'Layanan yang Anda cari tidak tersedia'}</p>
                    <Link to="/" className="btn-primary inline-flex items-center">
                        <FaArrowLeft className="mr-2" /> Kembali ke Beranda
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-[#F8FAFC] min-h-screen">
            {/* ============ MODAL POPUP SLIDER ============ */}
            {isModalOpen && images.length > 0 && (
                <div
                    className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeIn"
                    onClick={closeModal}
                >
                    {/* Konten Modal */}
                    <div
                        className="relative w-full max-w-5xl max-h-[90vh] flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Tombol Close */}
                        <button
                            onClick={closeModal}
                            className="absolute -top-12 right-0 md:-top-2 md:-right-12 w-10 h-10 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center backdrop-blur-sm transition-all z-10"
                            aria-label="Tutup"
                            title="Tutup (ESC)"
                        >
                            <FaTimes size={20} />
                        </button>

                        {/* Header Modal */}
                        <div className="text-white mb-3 flex items-center justify-between">
                            <div>
                                <h3 className="text-lg md:text-xl font-bold">
                                    {menu.name}
                                </h3>
                                <p className="text-xs opacity-70">
                                    {currentImageIndex + 1} / {images.length}
                                </p>
                            </div>
                        </div>

                        {/* Main Image */}
                        <div className="relative bg-black rounded-xl overflow-hidden group flex-1 flex items-center justify-center">
                            <img
                                src={getAssetUrl(images[currentImageIndex])}
                                alt={`${menu.name} - ${currentImageIndex + 1}`}
                                className="w-full h-full object-contain max-h-[70vh] transition-opacity duration-300"
                                onError={(e) => {
                                    e.target.src = 'https://via.placeholder.com/800x450?text=Gambar+Tidak+Tersedia';
                                }}
                            />

                            {/* Navigation Arrows — hanya jika > 1 gambar */}
                            {images.length > 1 && (
                                <>
                                    <button
                                        onClick={goToPrevImage}
                                        className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center backdrop-blur-sm transition-all"
                                        aria-label="Sebelumnya"
                                    >
                                        <FaChevronLeft />
                                    </button>
                                    <button
                                        onClick={goToNextImage}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center backdrop-blur-sm transition-all"
                                        aria-label="Berikutnya"
                                    >
                                        <FaChevronRight />
                                    </button>
                                </>
                            )}
                        </div>

                        {/* Thumbnails */}
                        {images.length > 1 && (
                            <div className="flex gap-2 mt-4 overflow-x-auto pb-2 justify-center">
                                {images.map((img, index) => (
                                    <button
                                        key={index}
                                        onClick={() => goToImage(index)}
                                        className={`flex-shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden border-2 transition-all ${currentImageIndex === index
                                                ? 'border-white ring-2 ring-white/50 scale-105'
                                                : 'border-white/20 hover:border-white/50 opacity-60 hover:opacity-100'
                                            }`}
                                        aria-label={`Gambar ${index + 1}`}
                                    >
                                        <img
                                            src={getAssetUrl(img)}
                                            alt={`Thumbnail ${index + 1}`}
                                            className="w-full h-full object-cover"
                                            onError={(e) => {
                                                e.target.src = 'https://via.placeholder.com/100?text=?';
                                            }}
                                        />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Hero Section */}
            <div
                className="relative text-white bg-cover bg-center bg-no-repeat min-h-[240px] md:min-h-[240px] max-h-[240px] flex items-center"
                style={{
                    backgroundImage: `url('/images/layanan.png')`,
                }}
            >
                {/* Overlay gradient ringan agar gambar tetap terlihat */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#0F4C3A]/70 via-[#0F4C3A]/45 to-[#2E7D32]/30"></div>

                {/* Konten di atas overlay */}
                <div className="relative container mx-auto px-4 py-16 md:py-20">
                    {/* Breadcrumb */}
                    <nav className="flex items-center text-sm mb-6 opacity-90">
                        <Link to="/" className="hover:text-[#4CAF50] transition-colors">Beranda</Link>
                        {menu.parent && (
                            <>
                                <FaChevronRight className="mx-2 text-xs" />
                                <span className="opacity-80">{menu.parent.name}</span>
                            </>
                        )}
                        <FaChevronRight className="mx-2 text-xs" />
                        <span className="font-semibold">{menu.name}</span>
                    </nav>

                    <div className="flex items-center gap-4 mb-4">
                        <div className="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                            <FaGlobe className="text-3xl" />
                        </div>
                        <div>
                            <span className="text-xs bg-white/20 px-3 py-1 rounded-full font-medium">
                                Layanan Eksternal
                            </span>
                            <h1 className="text-3xl md:text-4xl font-bold mt-2 drop-shadow-lg">
                                {menu.name}
                            </h1>
                        </div>
                    </div>

                    {menu.description && (
                        <p className="text-lg opacity-95 max-w-3xl drop-shadow-md">
                            {menu.description}
                        </p>
                    )}
                </div>
            </div>

            {/* Content */}
            <div className="container mx-auto px-4 py-12 max-w-5xl">
                {/* Informasi Penting */}
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 md:p-6 rounded-r-lg mb-8">
                    <div className="flex items-start">
                        <FaInfoCircle className="text-blue-500 text-xl mr-3 mt-1 flex-shrink-0" />
                        <div>
                            <h3 className="font-bold text-blue-800 mb-1">Informasi Penting</h3>
                            <p className="text-blue-700 text-sm">
                                Anda akan diarahkan ke website eksternal. Pastikan Anda sudah menyiapkan
                                semua dokumen dan persyaratan yang dibutuhkan sebelum melanjutkan.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Left Column - Main Content */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* 1. Prosedur / Langkah-langkah */}
                        {menu.steps && menu.steps.length > 0 && (
                            <div className="card-glass">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="w-12 h-12 bg-gradient-to-br from-[#0F4C3A] to-[#2E7D32] rounded-xl flex items-center justify-center">
                                        <FaClipboardList className="text-white text-xl" />
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-bold text-[#0F4C3A]">Prosedur Layanan</h2>
                                        <p className="text-sm text-gray-500">Ikuti langkah-langkah berikut</p>
                                    </div>
                                </div>

                                <ol className="space-y-4">
                                    {menu.steps.map((step, index) => (
                                        <li key={index} className="flex items-start gap-4 group">
                                            <span className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-[#0F4C3A] to-[#2E7D32] text-white rounded-lg flex items-center justify-center font-bold shadow-md">
                                                {index + 1}
                                            </span>
                                            <span className="text-gray-700 pt-2 leading-relaxed">
                                                {step}
                                            </span>
                                        </li>
                                    ))}
                                </ol>
                            </div>
                        )}

                        {/* 2. GALERI SLIDER INLINE */}
                        {images.length > 0 && (
                            <div className="card-glass">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 bg-gradient-to-br from-[#0F4C3A] to-[#2E7D32] rounded-xl flex items-center justify-center">
                                            <FaImage className="text-white text-xl" />
                                        </div>
                                        <div>
                                            <h2 className="text-xl font-bold text-[#0F4C3A]">Galeri Layanan</h2>
                                            <p className="text-sm text-gray-500">
                                                {images.length} gambar • {currentImageIndex + 1} / {images.length}
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setIsModalOpen(true)}
                                        className="text-xs md:text-sm text-[#2E7D32] hover:text-[#0F4C3A] font-medium flex items-center gap-1 px-3 py-2 rounded-lg hover:bg-[#2E7D32]/10 transition-colors"
                                    >
                                        <FaImage /> Buka Popup
                                    </button>
                                </div>

                                {/* Main Image */}
                                <div className="relative bg-gray-100 rounded-xl overflow-hidden group">
                                    <div className="aspect-video flex items-center justify-center">
                                        <img
                                            src={getAssetUrl(images[currentImageIndex])}
                                            alt={`${menu.name} - ${currentImageIndex + 1}`}
                                            className="w-full h-full object-contain max-h-[500px] transition-opacity duration-300"
                                            onError={(e) => {
                                                e.target.src = 'https://via.placeholder.com/800x450?text=Gambar+Tidak+Tersedia';
                                            }}
                                        />
                                    </div>

                                    {images.length > 1 && (
                                        <>
                                            <button
                                                onClick={goToPrevImage}
                                                className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center backdrop-blur-sm transition-all opacity-0 group-hover:opacity-100"
                                                aria-label="Sebelumnya"
                                            >
                                                <FaChevronLeft />
                                            </button>
                                            <button
                                                onClick={goToNextImage}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center backdrop-blur-sm transition-all opacity-0 group-hover:opacity-100"
                                                aria-label="Berikutnya"
                                            >
                                                <FaChevronRight />
                                            </button>

                                            <div className="absolute bottom-3 right-3 bg-black/60 text-white text-xs px-3 py-1.5 rounded-full backdrop-blur-sm">
                                                {currentImageIndex + 1} / {images.length}
                                            </div>
                                        </>
                                    )}
                                </div>

                                {images.length > 1 && (
                                    <div className="flex gap-2 mt-4 overflow-x-auto pb-2 scrollbar-thin">
                                        {images.map((img, index) => (
                                            <button
                                                key={index}
                                                onClick={() => goToImage(index)}
                                                className={`flex-shrink-0 w-20 h-20 md:w-24 md:h-24 rounded-lg overflow-hidden border-2 transition-all ${currentImageIndex === index
                                                    ? 'border-[#2E7D32] ring-2 ring-[#2E7D32]/30 scale-105'
                                                    : 'border-gray-200 hover:border-gray-300 opacity-70 hover:opacity-100'
                                                    }`}
                                                aria-label={`Gambar ${index + 1}`}
                                            >
                                                <img
                                                    src={getAssetUrl(img)}
                                                    alt={`Thumbnail ${index + 1}`}
                                                    className="w-full h-full object-cover"
                                                    onError={(e) => {
                                                        e.target.src = 'https://via.placeholder.com/100?text=?';
                                                    }}
                                                />
                                            </button>
                                        ))}
                                    </div>
                                )}

                                {menu.files && menu.files.length > 0 && (
                                    <div className="border-t pt-5 mt-5">
                                        {/* Header */}
                                        <div className="flex items-center justify-between mb-4">
                                            <h4 className="font-bold text-gray-800 text-sm flex items-center gap-2">
                                                <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-sm">
                                                    <FaFilePdf className="text-white text-xs" />
                                                </span>
                                                Dokumen Pendukung
                                            </h4>
                                            <span className="text-[10px] font-semibold text-red-600 bg-red-50 px-2 py-1 rounded-full">
                                                {menu.files.length} File
                                            </span>
                                        </div>

                                        {/* List Files */}
                                        <div className="space-y-2">
                                            {menu.files.map((file, idx) => (
                                                <a
                                                    key={idx}
                                                    href={file.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="group relative flex items-center gap-3 p-3 bg-gradient-to-r from-gray-50 to-white border border-gray-200 rounded-xl hover:border-red-300 hover:shadow-md hover:from-red-50 hover:to-white transition-all duration-200"
                                                >
                                                    {/* Icon PDF */}
                                                    <div className="relative flex-shrink-0">
                                                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform duration-200">
                                                            <FaFilePdf className="text-white text-sm" />
                                                        </div>
                                                        <span className="absolute -top-1 -right-1 w-4 h-4 bg-white rounded-full border-2 border-red-500 flex items-center justify-center">
                                                            <FaDownload className="text-red-500 text-[6px]" />
                                                        </span>
                                                    </div>

                                                    {/* Info File */}
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-xs font-semibold text-gray-800 truncate group-hover:text-red-600 transition-colors">
                                                            {file.name}
                                                        </p>
                                                        <div className="flex items-center gap-2 mt-0.5">
                                                            <span className="text-[10px] text-gray-400 font-medium">
                                                                PDF Document
                                                            </span>
                                                            <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                                                            <span className="text-[10px] text-gray-400 font-medium">
                                                                Klik untuk membuka
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {/* Arrow / Action Indicator */}
                                                    <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gray-100 group-hover:bg-red-500 flex items-center justify-center transition-all duration-200">
                                                        <FaExternalLinkAlt className="text-gray-400 group-hover:text-white text-xs transition-colors" />
                                                    </div>
                                                </a>
                                            ))}
                                        </div>

                                        {/* Footer Info */}
                                        <div className="mt-3 flex items-center gap-2 text-[10px] text-gray-400">
                                            <FaInfoCircle className="flex-shrink-0" />
                                            <span>Dokumen akan terbuka di tab baru</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* 3. Persyaratan */}
                        {menu.requirements && menu.requirements.length > 0 && (
                            <div className="card-glass">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="w-12 h-12 bg-gradient-to-br from-[#0F4C3A] to-[#2E7D32] rounded-xl flex items-center justify-center">
                                        <FaFileAlt className="text-white text-xl" />
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-bold text-[#0F4C3A]">Persyaratan</h2>
                                        <p className="text-sm text-gray-500">Dokumen yang perlu disiapkan</p>
                                    </div>
                                </div>

                                <ul className="space-y-3">
                                    {menu.requirements.map((req, index) => (
                                        <li key={index} className="flex items-start gap-3">
                                            <FaCheckCircle className="text-green-500 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">{req}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* 4. Notice / Catatan */}
                        {menu.notice && (
                            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 md:p-6 rounded-r-lg">
                                <div className="flex items-start">
                                    <FaExclamationTriangle className="text-yellow-600 text-xl mr-3 mt-1 flex-shrink-0" />
                                    <div>
                                        <h3 className="font-bold text-yellow-800 mb-1">Catatan</h3>
                                        <p className="text-yellow-700 text-sm">{menu.notice}</p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Right Column - Sidebar */}
                    <div className="lg:col-span-1">
                        <div className="card-glass sticky top-24">
                            <h3 className="font-bold text-[#0F4C3A] mb-4">Buka Layanan</h3>
                            <p className="text-sm text-gray-600 mb-6">
                                Klik tombol di bawah untuk membuka layanan di tab baru.
                            </p>

                            <button
                                onClick={handleOpenExternal}
                                className="w-full bg-gradient-to-r from-[#0F4C3A] to-[#2E7D32] text-white py-3 px-6 rounded-lg font-semibold hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2 mb-4"
                            >
                                Buka Layanan
                                <FaExternalLinkAlt className="text-sm" />
                            </button>

                            <div className="p-3 bg-gray-50 rounded-lg mb-4">
                                <p className="text-xs text-gray-500 mb-1">URL Tujuan:</p>
                                <p className="text-xs text-gray-700 break-all font-mono">
                                    {menu.external_url}
                                </p>
                            </div>

                            {menu.contact_info && (
                                <div className="border-t pt-4 mt-4">
                                    <h4 className="font-semibold text-gray-700 text-sm mb-2">Butuh Bantuan?</h4>
                                    <p className="text-xs text-gray-600">{menu.contact_info}</p>
                                </div>
                            )}

                            <div className="border-t pt-4 mt-4">
                                <h4 className="font-semibold text-gray-700 text-sm mb-3">Kontak BPKAD</h4>
                                <div className="space-y-2 text-xs">
                                    <div className="flex items-center gap-2 text-gray-600">
                                        <FaPhone className="text-[#2E7D32]" />
                                        <span>(0293) 123456</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-gray-600">
                                        <FaEnvelope className="text-[#2E7D32]" />
                                        <span>bpkad@magelangkota.go.id</span>
                                    </div>
                                </div>
                            </div>

                            <Link
                                to="/"
                                className="mt-6 w-full flex items-center justify-center gap-2 border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                            >
                                <FaArrowLeft className="text-xs" />
                                Kembali ke Beranda
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LayananRedirect;